import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FlightOffer } from "../../types/flight";
import { formatDuration, getStopsText } from "../../utils/flightUtils";
import { MovmoCheckoutButton } from "@movmo/express-checkout-button";
import "@movmo/express-checkout-button/dist/index.css";
import { useMockMode } from "../../hooks/useMockMode";
import { buildMovmoFlightOfferForPa } from "../../services/mockOffers";
import { useFlightSearch } from "../../hooks/useFlightSearch";
import { useLinkedTravelerState } from "../../hooks/linkedTravelerStore";

interface StoredFlightSelection {
    flight: FlightOffer;
    fareType: string;
    fareBrand: string;
    price: number;
    tripType: "one-way" | "round-trip" | "multi-city";
    dictionaries?: Record<string, any>;
}

const ShoppingCartPage: React.FC = () => {
    const navigate = useNavigate();
    const mockMode = useMockMode();
    // Pull passenger counts from the persisted search params so the mock
    // offer reflects what the user actually searched for. searchData lives
    // in sessionStorage and is populated when the user submits the booking
    // form on /philippine-airlines.
    const { searchData } = useFlightSearch();
    // Native personalization (MOVMO-508 PR7): this cart page has no separate
    // guest/contact form either — Movmo Express Checkout collects passenger
    // info in its own iframe. The card below surfaces the Movmo-linked traveler (read at landing) instead of
    // literally populating fields, mirroring AeroCheckout's affordance.
    const { traveler } = useLinkedTravelerState("pa");
    const [selectedFlightOffer, setSelectedFlightOffer] = useState<
        | {
              flight: FlightOffer;
              fareType: string;
              fareBrand: string;
              price: number;
          }
        | undefined
    >();
    const [tripType, setTripType] = useState<
        "one-way" | "round-trip" | "multi-city"
    >("one-way");
    const [isLoading, setIsLoading] = useState(true);
    const [dictionaries, setDictionaries] = useState<Record<string, any>>({});

    useEffect(() => {
        // Load selected flight from sessionStorage
        const storedSelection = sessionStorage.getItem("selectedFlightOffer");

        if (storedSelection) {
            try {
                const parsedSelection: StoredFlightSelection =
                    JSON.parse(storedSelection);
                setSelectedFlightOffer({
                    flight: parsedSelection.flight,
                    fareType: parsedSelection.fareType,
                    fareBrand: parsedSelection.fareBrand,
                    price: parsedSelection.price,
                });
                setTripType(parsedSelection.tripType || "one-way");
                setDictionaries(parsedSelection.dictionaries || {});
            } catch (error) {
                console.error("Error parsing stored flight selection:", error);
                // Redirect to search if data is corrupted
                navigate("/philippine-airlines", { replace: true });
            }
        } else {
            // No selection found, redirect to search
            navigate("/philippine-airlines", { replace: true });
        }

        setIsLoading(false);
    }, [navigate]);

    const handleFillPassengerDetails = () => {
        // Navigate to passenger details page
        console.log("Fill passenger details");
        // You can add actual navigation logic here
        // navigate("/philippine-airlines/passenger-details");
    };

    const formatPrice = (amount: number): string => {
        return amount.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    };

    // Amadeus API returns times in local timezone (not UTC)
    // So we don't need to convert - just format the time for display
    const formatTimeLocal = (dateStr: string): string => {
        // Parse the date string as local time (no timezone conversion)
        // PSS-offer format: "2025-09-16T04:45:00" is already 4:45 AM local time
        const [, timePart] = dateStr.split("T");
        const [hours, minutes] = timePart.split(":").map(Number);

        // Format for 12-hour display
        const period = hours >= 12 ? "PM" : "AM";
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        const displayMinutes = minutes.toString().padStart(2, "0");

        return `${displayHours}:${displayMinutes} ${period}`;
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        // Use Philippine timezone for date display
        const options: Intl.DateTimeFormatOptions = {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
            timeZone: "Asia/Manila",
        };
        return date.toLocaleDateString("en-US", options);
    };

    // Extract city names from airport codes
    const getCityName = (iataCode: string): string => {
        // This would normally come from dictionaries or a lookup
        const cityMap: Record<string, string> = {
            MNL: "Manila",
            CEB: "Cebu",
            DVO: "Davao",
            // Add more as needed
        };
        return cityMap[iataCode] || iataCode;
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0047ba] mx-auto"></div>
                    <p className="mt-4 text-gray-600">
                        Loading your selection...
                    </p>
                </div>
            </div>
        );
    }

    if (!selectedFlightOffer) {
        return null; // Will redirect in useEffect
    }

    const { flight, fareBrand, price } = selectedFlightOffer;
    const currency = flight.price.currency;
    const isRoundTrip =
        tripType === "round-trip" && flight.itineraries.length >= 2;

    // Get the branded fare label from the first segment's fare details
    const getBrandedFareLabel = (itineraryIndex: number = 0): string => {
        let label = fareBrand;
        if (
            flight.travelerPricings?.[0]?.fareDetailsBySegment?.[itineraryIndex]
        ) {
            label =
                flight.travelerPricings[0].fareDetailsBySegment[itineraryIndex]
                    .brandedFareLabel || fareBrand;
        }
        // Convert to title case (e.g., "COMFORT CLASS" -> "Comfort Class")
        return label
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    // The Amadeus provider was retired (MOVMO-590). The cart always hands
    // the canonical Movmo offer to the mock-pa adapter; `dictionaries` is
    // still read for the display helpers above.
    const hasSelectedOffer = Boolean(selectedFlightOffer);

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Blue Header with extended background */}
            <div className="bg-[#0047ba] w-full" style={{ height: "236px" }}>
                {/* Top navigation bar */}
                <div className="w-full">
                    <div className="max-w-[1200px] mx-auto">
                        <div
                            className="flex items-center justify-between py-3 px-4"
                            style={{ height: "50px" }}
                        >
                            {/* Logo */}
                            <Link to="/philippine-airlines">
                                <img
                                    src="/PR_results.svg"
                                    alt="Philippine Airlines"
                                    className="h-11 w-auto"
                                />
                            </Link>

                            {/* Language selector */}
                            <button className="text-white text-xs font-base px-4 py-1.5 border border-white rounded-full hover:bg-white hover:bg-opacity-10 transition-colors">
                                English ⌄
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content - overlaid on blue background */}
            <div
                className="max-w-[1200px] mx-auto px-4"
                style={{ marginTop: "-170px" }}
            >
                {/* Flight Details Card */}
                <div className="bg-[#fafcff] rounded-lg shadow-md p-8 pt-4">
                    {/* Warning Banner */}
                    <div className="rounded-lg p-4 border-l-4 border-[#c26e60] mb-6 shadow-md">
                        <div className="flex items-start gap-3">
                            <span className="text-[#c26e60] text-2xl">⚠</span>
                            <div>
                                <h3 className="text-[#c26e60] font-bold text-lg mb-1">
                                    1 warning
                                </h3>
                                <p className="text-gray-600 text-sm">
                                    Hurry up! Last seats available at this
                                    price.
                                </p>
                            </div>
                        </div>
                    </div>

                    <h2 className="text-xl font-bold mb-6 text-black">
                        Flight details
                    </h2>

                    {/* Departing Flight */}
                    <div className="mb-6">
                        <div className="bg-[#0047ba] text-white px-4 py-3 rounded-t-lg">
                            <h3 className="font-bold text-lg">
                                {getCityName(
                                    flight.itineraries[0].segments[0].departure
                                        .iataCode
                                )}{" "}
                                to{" "}
                                {getCityName(
                                    flight.itineraries[0].segments[
                                        flight.itineraries[0].segments.length -
                                            1
                                    ].arrival.iataCode
                                )}{" "}
                                -{" "}
                                {formatDate(
                                    flight.itineraries[0].segments[0].departure
                                        .at
                                )}
                            </h3>
                        </div>

                        <div className="border border-gray-200 rounded-b-lg p-6">
                            <div className="flex items-start">
                                {/* Left side - Time and route */}
                                <div className="flex-1 pr-8">
                                    {/* First row: Times and airport codes with flight path */}
                                    <div className="flex items-center mb-1">
                                        {/* Departure Time and Code */}
                                        <div className="min-w-[80px]">
                                            <div className="text-base font-bold text-black">
                                                {formatTimeLocal(
                                                    flight.itineraries[0]
                                                        .segments[0].departure
                                                        .at
                                                )}
                                            </div>
                                            <div className="text-sm font-medium text-gray-700">
                                                {
                                                    flight.itineraries[0]
                                                        .segments[0].departure
                                                        .iataCode
                                                }
                                            </div>
                                        </div>

                                        {/* Flight Path - aligned with times */}
                                        <div
                                            className="flex-1 flex items-center px-4"
                                            style={{ marginTop: "2px" }}
                                        >
                                            <span className="text-[#0047ba] text-xl">
                                                •
                                            </span>
                                            <div className="flex-1 border-t-2 border-dotted border-gray-400"></div>
                                            <span className="text-xs text-gray-600 px-2 py-1 bg-[#e6edf8] rounded-md whitespace-nowrap">
                                                {getStopsText(
                                                    flight.itineraries[0]
                                                        .segments
                                                )}
                                            </span>
                                            <div className="flex-1 border-t-2 border-dotted border-gray-400"></div>
                                            <span className="text-[#0047ba] text-xl">
                                                •
                                            </span>
                                        </div>

                                        {/* Arrival Time and Code */}
                                        <div className="min-w-[80px] text-right">
                                            <div className="text-base font-bold text-black">
                                                {formatTimeLocal(
                                                    flight.itineraries[0]
                                                        .segments[
                                                        flight.itineraries[0]
                                                            .segments.length - 1
                                                    ].arrival.at
                                                )}
                                            </div>
                                            <div className="text-sm font-medium text-gray-700">
                                                {
                                                    flight.itineraries[0]
                                                        .segments[
                                                        flight.itineraries[0]
                                                            .segments.length - 1
                                                    ].arrival.iataCode
                                                }
                                            </div>
                                        </div>
                                    </div>

                                    {/* Second row: Airport names and terminals */}
                                    <div className="flex justify-between">
                                        <div className="min-w-[80px]">
                                            <div className="text-sm text-gray-500">
                                                Ninoy Aquino International
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                Terminal{" "}
                                                {flight.itineraries[0]
                                                    .segments[0].departure
                                                    .terminal || "2"}
                                            </div>
                                        </div>
                                        <div className="min-w-[80px] text-right">
                                            <div className="text-sm text-gray-500">
                                                Mactan International
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                Terminal{" "}
                                                {flight.itineraries[0].segments[
                                                    flight.itineraries[0]
                                                        .segments.length - 1
                                                ].arrival.terminal || "1"}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Middle section - Flight Info */}
                                <div className="px-8 border-l border-gray-200 min-h-[85px]">
                                    <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                        <span>
                                            {formatDuration(
                                                flight.itineraries[0].duration
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-gray-600 mb-3">
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                        <span>
                                            PR{" "}
                                            {
                                                flight.itineraries[0]
                                                    .segments[0].number
                                            }{" "}
                                            operated by Philippine Airlines
                                        </span>
                                        <img
                                            src="https://content.airhex.com/content/logos/airlines_PR_20_20_s.png"
                                            alt="PR"
                                            className="w-4 h-4"
                                        />
                                    </div>
                                    <button className="text-[#0047ba] text-xs underline pl-6">
                                        See itinerary details
                                    </button>
                                </div>

                                {/* Right section - Fare Type Dropdown */}
                                <div className="min-w-[180px] pl-8 border-l border-gray-200 min-h-[100px] flex items-center">
                                    <button className="w-full flex items-center justify-between text-left gap-10">
                                        <span className="text-base font-semibold text-[#01296a]">
                                            {getBrandedFareLabel(0)}
                                        </span>
                                        <svg
                                            className="w-5 h-5 text-[#01296a] flex-shrink-0"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 9l-7 7-7-7"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Returning Flight (if round-trip) */}
                    {isRoundTrip && (
                        <div className="mb-6">
                            <div className="bg-[#0047ba] text-white px-4 py-3 rounded-t-lg">
                                <h3 className="font-bold text-lg">
                                    {getCityName(
                                        flight.itineraries[1].segments[0]
                                            .departure.iataCode
                                    )}{" "}
                                    to{" "}
                                    {getCityName(
                                        flight.itineraries[1].segments[
                                            flight.itineraries[1].segments
                                                .length - 1
                                        ].arrival.iataCode
                                    )}{" "}
                                    -{" "}
                                    {formatDate(
                                        flight.itineraries[1].segments[0]
                                            .departure.at
                                    )}
                                </h3>
                            </div>

                            <div className="border border-gray-200 rounded-b-lg p-6">
                                <div className="flex items-start justify-between">
                                    {/* Left side - Time and route */}
                                    <div className="flex-1 pr-8">
                                        {/* First row: Times and airport codes with flight path */}
                                        <div className="flex items-center mb-1">
                                            {/* Departure Time and Code */}
                                            <div className="min-w-[80px]">
                                                <div className="text-base font-bold text-black">
                                                    {formatTimeLocal(
                                                        flight.itineraries[1]
                                                            .segments[0]
                                                            .departure.at
                                                    )}
                                                </div>
                                                <div className="text-sm font-medium text-gray-700">
                                                    {
                                                        flight.itineraries[1]
                                                            .segments[0]
                                                            .departure.iataCode
                                                    }
                                                </div>
                                            </div>

                                            {/* Flight Path - aligned with times */}
                                            <div
                                                className="flex-1 flex items-center px-4"
                                                style={{ marginTop: "2px" }}
                                            >
                                                <span className="text-[#0047ba] text-xl">
                                                    •
                                                </span>
                                                <div className="flex-1 border-t-2 border-dotted border-gray-400"></div>
                                                <span className="text-xs text-gray-600 px-2 py-1 bg-[#e6edf8] rounded-md whitespace-nowrap">
                                                    {getStopsText(
                                                        flight.itineraries[1]
                                                            .segments
                                                    )}
                                                </span>
                                                <div className="flex-1 border-t-2 border-dotted border-gray-400"></div>
                                                <span className="text-[#0047ba] text-xl">
                                                    •
                                                </span>
                                            </div>

                                            {/* Arrival Time and Code */}
                                            <div className="min-w-[80px] text-right">
                                                <div className="text-base font-bold text-black">
                                                    {formatTimeLocal(
                                                        flight.itineraries[1]
                                                            .segments[
                                                            flight
                                                                .itineraries[1]
                                                                .segments
                                                                .length - 1
                                                        ].arrival.at
                                                    )}
                                                </div>
                                                <div className="text-sm font-medium text-gray-700">
                                                    {
                                                        flight.itineraries[1]
                                                            .segments[
                                                            flight
                                                                .itineraries[1]
                                                                .segments
                                                                .length - 1
                                                        ].arrival.iataCode
                                                    }
                                                </div>
                                            </div>
                                        </div>

                                        {/* Second row: Airport names and terminals */}
                                        <div className="flex justify-between">
                                            <div className="min-w-[80px]">
                                                <div className="text-sm text-gray-500">
                                                    Mactan International
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    Terminal{" "}
                                                    {flight.itineraries[1]
                                                        .segments[0].departure
                                                        .terminal || "1"}
                                                </div>
                                            </div>
                                            <div className="min-w-[80px] text-right">
                                                <div className="text-sm text-gray-500">
                                                    Ninoy Aquino International
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    Terminal{" "}
                                                    {flight.itineraries[1]
                                                        .segments[
                                                        flight.itineraries[1]
                                                            .segments.length - 1
                                                    ].arrival.terminal || "2"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Middle section - Flight Info */}
                                    <div className="px-8 border-l border-gray-200 min-h-[85px]">
                                        <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                            <span>
                                                {formatDuration(
                                                    flight.itineraries[1]
                                                        .duration
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-gray-600 mb-3">
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                            <span>
                                                PR{" "}
                                                {
                                                    flight.itineraries[1]
                                                        .segments[0].number
                                                }{" "}
                                                operated by Philippine Airlines
                                            </span>
                                            <img
                                                src="https://content.airhex.com/content/logos/airlines_PR_20_20_s.png"
                                                alt="PR"
                                                className="w-4 h-4"
                                            />
                                        </div>
                                        <button className="text-[#0047ba] text-xs underline pl-6">
                                            See itinerary details
                                        </button>
                                    </div>

                                    {/* Right section - Fare Type Dropdown */}
                                    <div className="min-w-[180px] pl-8 border-l border-gray-200 min-h-[100px] flex items-center">
                                        <button className="w-full flex items-center justify-between text-left gap-10">
                                            <span className="text-base font-semibold text-[#01296a]">
                                                {getBrandedFareLabel(1)}
                                            </span>
                                            <svg
                                                className="w-5 h-5 text-[#01296a] flex-shrink-0"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 9l-7 7-7-7"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Price Summary */}
                    <div className="pt-3">
                        <div className="flex items-center justify-end mb-4">
                            <div className="text-right">
                                <div className="flex items-baseline gap-2 justify-end">
                                    <span className="text-lg text-[#0047bb] font-medium">
                                        Total price: {currency}
                                    </span>
                                    <span className="text-3xl font-bold text-[#0047ba]">
                                        {formatPrice(price)}
                                    </span>
                                </div>
                                <div className="text-sm text-gray-500 mt-1 font-medium">
                                    (including taxes, fees and discounts)
                                </div>
                                <button className="text-[#0047ba] text-sm underline">
                                    See price details.
                                </button>
                            </div>
                        </div>

                        {/* Policy Links */}
                        <div className="flex gap-1 text-sm mb-6 justify-end">
                            <button className="text-[#0047ba] underline">
                                Detailed baggage policy
                            </button>
                            <span className="text-[#0047ba] font-medium">
                                |
                            </span>
                            <button className="text-[#0047ba] underline">
                                Fare Rules, Cancellation and Refund policy
                            </button>
                            <span className="text-[#0047ba] font-medium">
                                |
                            </span>
                            <button className="text-[#0047ba] underline">
                                Dangerous goods policy
                            </button>
                        </div>

                        {/* Movmo-linked traveler affordance (MOVMO-508) */}
                        {traveler?.profile && (
                            <div className="flex items-center justify-end gap-2 mb-4">
                                <span className="text-[11px] font-semibold text-white bg-[#0047ba] rounded-full px-2 py-0.5">
                                    from Movmo
                                </span>
                                <span className="text-sm text-gray-700">
                                    Linked with Movmo as{" "}
                                    <strong>
                                        {[
                                            traveler.profile.first_name,
                                            traveler.profile.last_name,
                                        ]
                                            .filter(Boolean)
                                            .join(" ") ||
                                            traveler.profile.email}
                                    </strong>{" "}
                                    — your saved details will carry through to
                                    Movmo Express Checkout.
                                </span>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-4">
                            {/* Movmo Express Checkout Button */}
                            {hasSelectedOffer && selectedFlightOffer && (
                                <MovmoCheckoutButton
                                    iataCode="PR"
                                    apiKey={
                                        process.env.REACT_APP_MOVMO_API_KEY ||
                                        ""
                                    }
                                    providerID="mock-pa"
                                    flightOffer={buildMovmoFlightOfferForPa(
                                        selectedFlightOffer.flight,
                                        {
                                            adults:
                                                searchData?.searchParams
                                                    .adults ?? 1,
                                            children:
                                                searchData?.searchParams
                                                    .children ?? 0,
                                            infants:
                                                searchData?.searchParams
                                                    .infants ?? 0,
                                        },
                                    )}
                                />
                            )}

                            <button
                                onClick={handleFillPassengerDetails}
                                className="px-8 py-3 bg-[#0047ba] text-white rounded-lg font-bold hover:bg-blue-700 transition-colors"
                            >
                                Fill passenger details
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShoppingCartPage;
